import PropTypes from 'prop-types'
import { Button, Switch } from 'antd'
import { FileSearchOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import { formatDOB } from '../../../lib/datetime'
import { toPath } from '../../../routes'
import DownloadLink from '../../Common/DownloadLink'
import CircleCheckIcon from '../../Common/CircleCheckIcon'
import PatientEntry from '../../Common/PatientEntry'

// Widths of antd's selection and expand gutter columns. The expanded details are
// indented by their sum so the fields line up with the card above them.
export const EXPAND_COLUMN_WIDTH = 40
export const SELECTION_COLUMN_WIDTH = 40

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
    // PatientEntry sets the link to nowrap for the desktop table; on mobile we
    // let a long name wrap instead of overflowing.
    '& a': {
      whiteSpace: 'normal !important',
    },
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  // Authorized switch + view link share the last line. Left-aligned rather than
  // space-between: the switch label swaps between "Authorized" and "Not
  // Authorized", so pinning it right made the edge jitter from row to row.
  actionsLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 14,
    marginTop: 4,
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    color: '#8c8c8c',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldValue: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
  spanTwo: {
    gridColumn: '1 / -1',
  },
})

function Field({ label, children = null, span = false }) {
  const classes = useStyles()
  return (
    <div
      className={span ? `${classes.field} ${classes.spanTwo}` : classes.field}
    >
      <span className={classes.fieldLabel}>{label}</span>
      <span className={classes.fieldValue}>{children}</span>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  span: PropTypes.bool,
}

// Mirrors the desktop PDF/Full columns: a dash when the flag is unknown.
function FileStatus({ value = null, warningColor = undefined }) {
  if (value === null || value === undefined) return <span>-</span>
  return (
    <CircleCheckIcon checked={value} falseStyle={{ color: warningColor }} />
  )
}

FileStatus.propTypes = {
  value: PropTypes.bool,
  warningColor: PropTypes.string,
}

// Same rule as the desktop table: an inactive authorization or an inactive
// patient may not have its authorization toggled.
function isInactive(officeAuthorization) {
  return (
    officeAuthorization.active === false ||
    officeAuthorization.patient?.active === false
  )
}

export function OfficeAuthorizationMobileCard({
  officeAuthorization,
  practiceId,
  isAdmin = false,
  warningColor = undefined,
  onAuthorizedChange,
}) {
  const classes = useStyles()
  const { device, patient, id } = officeAuthorization
  // formatDOB(undefined) would render today's date, so only format a real value.
  const dob = patient?.birthDate ? formatDOB(patient.birthDate) : null
  const detailLine = [dob, device?.deviceModel?.deviceType?.name]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className={classes.card}>
      <div className={classes.name}>
        {patient ? <PatientEntry patient={patient} /> : '—'}
      </div>
      {detailLine && <div className={classes.line}>{detailLine}</div>}
      {isAdmin && (
        <div className={classes.line}>
          PDF{' '}
          <FileStatus
            value={officeAuthorization.hasReportFile}
            warningColor={warningColor}
          />{' '}
          · Full{' '}
          <FileStatus
            value={officeAuthorization.hasReportRawFile}
            warningColor={warningColor}
          />
        </div>
      )}
      <div className={classes.actionsLine}>
        <Switch
          checked={officeAuthorization.authorized}
          disabled={isInactive(officeAuthorization)}
          checkedChildren="Authorized"
          unCheckedChildren="Not&nbsp;Authorized"
          onChange={authorized => onAuthorizedChange(id, authorized)}
        />
        <Link
          to={`${toPath.billing.officeAuthorizations.officeAuthorization({
            id,
          })}?practiceId=${practiceId}`}
        >
          <Button
            type="link"
            size="small"
            icon={<FileSearchOutlined />}
            style={{ paddingLeft: 0 }}
          >
            View
          </Button>
        </Link>
      </div>
    </div>
  )
}

OfficeAuthorizationMobileCard.propTypes = {
  officeAuthorization: PropTypes.shape({
    id: PropTypes.string.isRequired,
    active: PropTypes.bool,
    authorized: PropTypes.bool,
    hasReportFile: PropTypes.bool,
    hasReportRawFile: PropTypes.bool,
    patient: PropTypes.shape({
      active: PropTypes.bool,
      birthDate: PropTypes.string,
    }),
    device: PropTypes.shape({
      deviceModel: PropTypes.shape({
        deviceType: PropTypes.shape({
          name: PropTypes.string,
        }),
      }),
    }),
  }).isRequired,
  practiceId: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool,
  warningColor: PropTypes.string,
  onAuthorizedChange: PropTypes.func.isRequired,
}

export function OfficeAuthorizationMobileDetails({
  officeAuthorization,
  indent = 0,
}) {
  const classes = useStyles()
  const {
    billingDate,
    cptCode,
    icd10Code,
    insuranceAuthorizationFilePath,
    insuranceAuthorizationNumber,
    patient,
  } = officeAuthorization

  // Same as the desktop "Auth #" column: the number links to the file when one
  // was uploaded, otherwise it is plain text.
  const authNumber = insuranceAuthorizationFilePath ? (
    <DownloadLink path={insuranceAuthorizationFilePath}>
      <span>{insuranceAuthorizationNumber}</span>
    </DownloadLink>
  ) : (
    insuranceAuthorizationNumber || '—'
  )

  return (
    <div className={classes.detailsGrid} style={{ paddingLeft: indent }}>
      <Field label="MRN">{patient?.practiceMrn || '—'}</Field>
      <Field label="Billing date">{billingDate || '—'}</Field>
      <Field label="CPT code">{cptCode || '—'}</Field>
      <Field label="ICD-10 code">{icd10Code || '—'}</Field>
      <Field label="Auth #" span>
        {authNumber}
      </Field>
    </div>
  )
}

OfficeAuthorizationMobileDetails.propTypes = {
  indent: PropTypes.number,
  officeAuthorization: PropTypes.shape({
    billingDate: PropTypes.string,
    cptCode: PropTypes.string,
    icd10Code: PropTypes.string,
    insuranceAuthorizationNumber: PropTypes.string,
    insuranceAuthorizationFilePath: PropTypes.string,
    patient: PropTypes.shape({
      practiceMrn: PropTypes.string,
    }),
  }).isRequired,
}
